import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { useToastContext } from '@context/toastContext';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName, selectSelectedFeatures } from '@redux/selectors/selectors';
import DialogComponent from '@components/DialogComponent/DialogComponent';
import './group-selection-operations.scss';
import GroupSelectionOperationCard from '@components/GroupSelectionOperations/GroupSelectionOperationCard';
import EntityForm from '@features/entities/entity-form/ui/EntityForm';
import { useEntityLoader } from '@features/entities/entity-form/model/useEntityLoader';
import { useOutletContext } from 'react-router-dom';
import { GroupSelectionOutletContext } from '@features/group-selection/model/types';
import { groupUpdateApi } from '@api/groupUpdateApi/groupUpdateApi';
import { GroupOperation, GroupOperationInfo } from '@api/groupUpdateApi/types';
import { FormDataObj } from '@utils/types';
import { useDispatch } from 'react-redux';
import { setSourceReloadRequired } from '@redux/map/map-reducer';
import { Trans, useTranslation } from 'react-i18next';

const GROUP_OPERATION_TO_API_METHOD: GroupOperation = {
    ADD_DEDICATED_ACCESS: 'groupAddDedicatedAccess',
    ADD_MODES: 'groupAddModes',
    ADD_LANES: 'groupAddLanes',
    REMOVE_LANES: 'groupRemoveLanes',
};

const GroupSelectionOperations = () => {
    const [operations, setOperations] = useState<GroupOperationInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeOperation, setActiveOperation] = useState<GroupOperationInfo | null>(null);
    const { addToast } = useToastContext();
    const selectedFeatures = useTypedSelector(selectSelectedFeatures);
    const { sourceEntityName, groupUpdateTaskArgument } =
        useOutletContext<GroupSelectionOutletContext>();
    const dispatch = useDispatch();
    const sourceId = useTypedSelector(state =>
        selectMapLayerIdByEntityName(state, sourceEntityName)
    );
    const {
        entity,
        metadata,
        loadData,
        changeForm,
        loading: isFormLoading,
    } = useEntityLoader<any>({
        entityName: activeOperation?.metadataEntityName ?? '',
        groupUpdateTaskArgument,
    });
    const { t } = useTranslation();

    useEffect(() => {
        const abortController = new AbortController();
        void (async () => {
            setLoading(true);
            const resp = await groupUpdateApi.listOperations(abortController.signal);
            if (isErrorResponse(resp)) {
                try {
                    const parsedErr =
                        typeof resp.error === 'string'
                            ? safelyParseJson(resp.error)?.error
                            : resp.error.error;
                    addToast(parsedErr, 'error');
                } catch (e) {
                    console.log('Replan. Error parsing error response', resp, e);
                }
            } else {
                setOperations(resp);
            }
            setLoading(false);
        })();

        return () => {
            abortController.abort();
        };
    }, [addToast]);

    useEffect(() => {
        if (!activeOperation) {
            return;
        }

        void loadData({ entityName: activeOperation.metadataEntityName });
    }, [activeOperation, loadData]);

    const selectedItemsText = useMemo(() => {
        return selectedFeatures.length
            ? `${selectedFeatures.length} selected items`
            : 'no selected items';
    }, [selectedFeatures.length]);

    const onDialogClose = useCallback(() => {
        setActiveOperation(null);
    }, [setActiveOperation]);

    const submitFn = useCallback(
        async (formDataObj: FormDataObj) => {
            if (!activeOperation) {
                return;
            }

            const methodName = GROUP_OPERATION_TO_API_METHOD[activeOperation.operationId];
            const apiMethod = groupUpdateApi[methodName] as (
                data: { task: { source: FormDataObj; targets: string[] } },
                abortSignal: AbortSignal
            ) => Promise<any>;
            const resp = await apiMethod(
                {
                    task: {
                        source: formDataObj,
                        targets: selectedFeatures.map(({ id }) => `${id}`),
                    },
                },
                new AbortController().signal
            );

            if (isErrorResponse(resp)) {
                try {
                    const parsedErr =
                        typeof resp.error === 'string'
                            ? safelyParseJson(resp.error)?.error
                            : resp.error.error;
                    addToast(parsedErr, 'error');
                } catch (e) {
                    console.log('Replan. Error parsing error response', resp, e);
                }
                return;
            }
            addToast(`${t('common.save-successful')}`, 'success');

            dispatch(setSourceReloadRequired({ sourceId, isReloadRequired: true }));
            onDialogClose();
        },
        [activeOperation, addToast, dispatch, loadData, onDialogClose, selectedFeatures, sourceId]
    );

    return (
        <div className="group-selection-operations">
            <div className="group-selection-operations-info">
                <InfoOutlinedIcon fontSize="small" />
                <div>
                    <Trans
                        i18nKey="road-network.group-selection-operations.info-block"
                        components={{
                            title: <p className="group-selection-operations-info-title" />,
                            text1: <p />,
                            text2: <p />,
                        }}
                    />
                </div>
            </div>

            <div className="group-selection-operations-title">
                <p className="group-selection-operations-heading">Available operations</p>
                <p className="group-selection-operations-subheading">
                    {selectedFeatures.length
                        ? `Choose an operation to apply to the ${selectedItemsText}.`
                        : 'No selected items'}
                </p>
            </div>

            <div className="group-selection-operations-list">
                {loading ? (
                    <div className="group-selection-operations-empty">Loading operations...</div>
                ) : operations.length ? (
                    operations.map(operation => (
                        <GroupSelectionOperationCard
                            key={operation.operationId}
                            operation={operation}
                            description={t(
                                `road-network.group-selection-operations.${operation.operationId}`
                            )}
                            setActiveOperation={setActiveOperation}
                            disabled={!selectedFeatures.length}
                        />
                    ))
                ) : (
                    <div className="group-selection-operations-empty">No operations available.</div>
                )}
            </div>

            <DialogComponent
                open={!!activeOperation}
                onCloseFn={onDialogClose}
                dialogTitle={activeOperation?.printedName ?? ''}
            >
                <div className="group-selection-operations-modal-placeholder" />
                {activeOperation && metadata ? (
                    <EntityForm
                        loading={isFormLoading}
                        entity={entity}
                        metadata={metadata}
                        changeFormFn={changeForm}
                        saveObjFn={submitFn}
                        showDeleteBtn={false}
                    />
                ) : null}
            </DialogComponent>
        </div>
    );
};

export default GroupSelectionOperations;
